import {CheckCircleFillIcon, CheckCircleIcon, StopIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, IconButton} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {ConversationHeader} from './ConversationHeader'
import {useRef, useState} from 'react'

import styles from './ThreadHeader.module.css'
import {useResolveThreadMutation} from '../../mutations/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '../../mutations/use-unresolve-thread-mutation'
import type {fileLevelCommentKey, LineKey} from '@github-ui/diff-lines/types'
import {
  isCopilotCodeReviewThread,
  RESOLUTION_REASON_LABELS,
  type ResolutionReasonType,
  type Thread,
} from '@github-ui/diff-lines/conversation-types'
import {ResolveThreadSplitButton} from '@github-ui/diff-lines/resolve-thread-split-button'
import {isLoggedIn} from '@github-ui/client-env'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../page-data/loaders/use-thread-previews'

type ThreadHeaderProps = {
  firstCommentId?: number | null
  href?: string
  isCollapsed: boolean
  onNavigateToDiffComment: () => void
  onToggleCollapsed: () => void
  thread: Thread
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  isResolved: boolean
  isOutdated: boolean
  lineKey: LineKey | typeof fileLevelCommentKey
  resolutionReason?: string | null
}

export function ThreadHeader({
  firstCommentId,
  href,
  isCollapsed,
  onToggleCollapsed,
  onNavigateToDiffComment,
  thread,
  headingLevel,
  isResolved,
  isOutdated,
  lineKey,
  resolutionReason,
}: ThreadHeaderProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const shouldFocusUnresolveButtonRef = useRef(false)
  const {filePath} = getThreadPreviewsLineAndFileKeysFromThread(thread)
  const displayFilePath = thread.path ?? thread.positioning?.path ?? ''
  const line = thread.line ?? thread.positioning?.endLine
  const startLine =
    thread.positioning?.startLine && thread.positioning?.startLine !== thread.positioning?.endLine
      ? thread.positioning?.startLine
      : null
  const canResolveConversation = isLoggedIn()
  const resolutionReasonsEnabled = isFeatureEnabled('thread_resolution_reason') && isCopilotCodeReviewThread(thread)
  const showResolutionReasonSelector = resolutionReasonsEnabled && !isResolved
  const resolutionReasonLabel =
    resolutionReasonsEnabled && resolutionReason
      ? RESOLUTION_REASON_LABELS[resolutionReason as ResolutionReasonType]
      : undefined

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(filePath, lineKey)
  const handleResolveThread = (selectedResolutionReason?: ResolutionReasonType) => {
    if (isResolved) return

    shouldFocusUnresolveButtonRef.current = showResolutionReasonSelector
    resolveThreadMutation(
      {threadId: thread.id, resolutionReason: selectedResolutionReason},
      {
        onError: () => {
          shouldFocusUnresolveButtonRef.current = false
          setErrorMessage('Failed to resolve thread')
        },
        onSuccess: () => {
          if (!isCollapsed) onToggleCollapsed()
        },
      },
    )
  }

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(filePath, lineKey)
  const handleUnresolveThread = async () => {
    if (!thread) return
    if (!isResolved) return

    unresolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          if (isCollapsed) onToggleCollapsed()
        },
      },
    )

    sendPullRequestAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  return (
    <>
      <ConversationHeader
        firstCommentId={firstCommentId}
        href={href}
        isCollapsed={isCollapsed}
        isOutdated={isOutdated}
        isResolved={isResolved}
        startLine={startLine}
        line={line}
        path={displayFilePath}
        resolvedLabel={resolutionReasonLabel ? `Resolved as ${resolutionReasonLabel}` : undefined}
        rightSideContent={
          canResolveConversation && showResolutionReasonSelector ? (
            <ResolveThreadSplitButton resolveButtonLabel="Resolve comment" onResolve={handleResolveThread} />
          ) : canResolveConversation ? (
            <IconButton
              ref={(button: HTMLButtonElement | null) => {
                if (button && shouldFocusUnresolveButtonRef.current) {
                  shouldFocusUnresolveButtonRef.current = false
                  button.focus()
                }
              }}
              aria-label={isResolved ? 'Unresolve conversation' : 'Resolve conversation'}
              tooltipDirection="sw"
              icon={isResolved ? CheckCircleFillIcon : CheckCircleIcon}
              // need to be specific in order to override default IconButton color
              data-thread-resolved={isResolved}
              className={styles.ThreadHeaderIconButton}
              variant="invisible"
              onClick={isResolved ? handleUnresolveThread : () => handleResolveThread()}
            />
          ) : null
        }
        onNavigateToDiffComment={onNavigateToDiffComment}
        onToggleCollapsed={onToggleCollapsed}
        threadId={thread.id}
        headingLevel={headingLevel}
      />
      {isResolved && thread.resolvedBy && (
        <div className="fgColor-muted f6 mt-1">
          {resolutionReasonLabel ? (
            <span>
              <b>{thread.resolvedBy}</b> resolved this thread as <b>{resolutionReasonLabel}</b>
            </span>
          ) : (
            <span>
              <b>{thread.resolvedBy}</b> marked this conversation as resolved.
            </span>
          )}
        </div>
      )}
      {errorMessage && (
        <Flash variant="danger" className="m-2">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </>
  )
}
